import React from "react";
import {
  Award,
  Globe2,
  Landmark,
  FlaskConical,
  HeartHandshake,
  ArrowUpRight,
} from "lucide-react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import {
  Container,
  Section,
  SectionHeading,
  Eyebrow,
  Card,
  Button,
  Reveal,
} from "../Components/UI";

const awards = [
  {
    title: "Young Innovator & Entrepreneur",
    issuer: "MY Bharat Youth Convention",
    year: "2026 · New Delhi",
    text: "Recognised at the 'Youth for Viksit Bharat' Convention — in the presence of Union Minister Dr. Mansukh Mandaviya, Delhi CM Rekha Gupta and boAt co-founder Aman Gupta.",
  },
  {
    title: "Our Power, Our Planet Stars 2026",
    issuer: "EarthDay.org",
    year: "2026 · Global",
    text: "First international honour — recognised alongside ITC, Jio, the Indian Coast Guard and Envision.",
  },
  {
    title: "Elevate 2025 — Deep Tech / ESDM",
    issuer: "Government of Karnataka",
    year: "2025",
    text: "Awarded by Minister Priyank Kharge, with a grant of up to ₹25 lakh.",
  },
  {
    title: "Spontan Award",
    issuer: "Mercedes-Benz R&D India",
    year: "2025",
    text: "For driving innovation and streamlining engineering workflows.",
  },
  {
    title: "Honoured by the Indian Coast Guard",
    issuer: "Indian Coast Guard",
    year: "Honour",
    text: "Recognised on board an ICG vessel for clean-energy contribution.",
  },
  {
    title: "Published Research Paper",
    issuer: "IEEE",
    year: "Research",
    text: "Peer-reviewed work in technology and innovation.",
  },
  {
    title: "Viksit Bharat Young Leaders Dialogue",
    issuer: "Govt. of India",
    year: "Featured ×2",
    text: "Recognised twice on the nation's flagship Young Leaders Dialogue platform.",
  },
];

const policy = [
  {
    name: "Ministry of New & Renewable Energy",
    role: "Presented DevAnanta's clean energy-storage progress",
  },
  { name: "Ministry of Defence", role: "Engaged on energy & defence innovation" },
  {
    name: "Ministry of Youth Affairs & Sports",
    role: "Featured on the Viksit Bharat Young Leaders Dialogue",
  },
  { name: "Ministry of Railways", role: "Explored clean-energy applications for rail" },
  {
    name: "Government of Karnataka",
    role: "Elevate 2025 — deep-tech recognition & grant",
  },
  {
    name: "Ministry of External Affairs",
    role: "India delegate, BRICS 2026 youth working group",
  },
];

const gallery = [
  { src: "/img/brics-delegation.jpg", alt: "BRICS youth delegation" },
  { src: "/img/award-earthday.jpg", alt: "Receiving the EarthDay.org award" },
  { src: "/img/mybharat-2.jpg", alt: "On stage at the MY Bharat Youth Convention" },
  { src: "/img/ev-award.jpg", alt: "Recognised by officials" },
  { src: "/img/ev-lab.jpg", alt: "In the research lab" },
  { src: "/img/brics-panel.jpg", alt: "Speaking at the BRICS panel" },
  { src: "/img/ev-intl.jpg", alt: "International meeting" },
  { src: "/authorities/yld.jpg", alt: "Award recognition" },
  { src: "/img/ev-stage.jpg", alt: "At a national event" },
  { src: "/img/coastguard.jpg", alt: "Honoured by the Indian Coast Guard" },
  { src: "/img/isro.jpg", alt: "At the ISRO Space Applications Centre" },
  { src: "/img/ev-walk.jpg", alt: "Unity & plantation walk" },
  { src: "/img/ev-meet.jpg", alt: "In conversation" },
  { src: "/img/brics-group.jpg", alt: "BRICS delegates" },
  { src: "/authorities/railwayministry.jpg", alt: "Ministry engagement" },
  { src: "/img/ev-expo.jpg", alt: "Innovation expo" },
  { src: "/img/ev-elders.jpg", alt: "With community elders" },
  { src: "/img/policy-mnre.jpg", alt: "At the Ministry of New & Renewable Energy" },
  { src: "/authorities/vicepred.jpg", alt: "National youth forum" },
  { src: "/img/ev-forum.jpg", alt: "Young Indians forum" },
  { src: "/img/ev-port.jpg", alt: "At the shipyard" },
  { src: "/authorities/sanjayseth.jpg", alt: "Meeting national leaders" },
  { src: "/authorities/pralhadjosi.jpeg", alt: "Grassroots engagement" },
  { src: "/authorities/vivekanandji.jpg", alt: "Cultural & community engagement" },
  { src: "/authorities/rekha.jpg", alt: "On-ground outreach" },
  { src: "/authorities/yifi.jpg", alt: "Felicitation with officials" },
  { src: "/img/brics-speaking.jpg", alt: "On the BRICS panel" },
  { src: "/img/ev-flags.jpg", alt: "With the BRICS delegation" },
  { src: "/img/felicitation.jpg", alt: "Felicitation" },
];

const RecognitionPage: React.FC = () => {
  return (
    <main className="bg-ink-950 pt-16 md:pt-20">
      <Navbar />

      <Section className="!pb-12">
        <Container>
          <Reveal>
            <SectionHeading
              eyebrow="Recognition & Impact"
              title="Proof that the mission matters"
              intro="From national awards to global stages, ministry meetings to village roads — here's the work, and the recognition it has earned."
            />
          </Reveal>
        </Container>
      </Section>

      {/* AWARDS */}
      <Section className="!pt-0">
        <Container>
          <div className="mb-8 flex items-center gap-3">
            <Award className="h-6 w-6 text-gold-400" />
            <h2 className="font-display text-2xl font-bold text-white">
              Awards & honours
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {awards.map((a, i) => (
              <Reveal key={a.title} delay={i * 70}>
                <Card className="h-full p-6">
                  <div className="flex items-center justify-between">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-gold-500/10 text-gold-400">
                      <Award className="h-5 w-5" />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-gold-400">
                      {a.year}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold leading-snug text-white">
                    {a.title}
                  </h3>
                  <p className="mt-1 text-sm font-medium text-brand-400">{a.issuer}</p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{a.text}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* BRICS FEATURE */}
      <Section className="bg-ink-900">
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <img
                  src="/img/brics-solo.jpg"
                  alt="Devyansh Agrawal at BRICS 2026"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <Eyebrow>
                <Globe2 className="h-3.5 w-3.5" /> International
              </Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Representing India at BRICS 2026
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-zinc-300">
                I was one of five delegates chosen to represent India at the BRICS
                2026 Youth Entrepreneurship Working Group in Indore — themed
                <em> “From Local Innovation to Global Impact.”</em>
              </p>
              <p className="mt-4 leading-relaxed text-zinc-400">
                Alongside young founders and policymakers from 8+ nations — Brazil,
                China, Russia, South Africa and more — we shaped ideas on rural
                entrepreneurship, green business and global partnerships for the next
                generation.
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* POLICY */}
      <Section>
        <Container>
          <div className="mb-8 flex items-center gap-3">
            <Landmark className="h-6 w-6 text-brand-400" />
            <h2 className="font-display text-2xl font-bold text-white">
              Policy & institutions
            </h2>
          </div>
          <p className="mb-8 max-w-2xl text-zinc-400">
            Turning innovation into national impact means working hand-in-hand with
            those who shape policy. The ministries and institutions I've engaged with:
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {policy.map((p, i) => (
              <Reveal key={p.name} delay={i * 60}>
                <Card className="h-full p-5">
                  <h3 className="font-display text-base font-semibold text-white">
                    {p.name}
                  </h3>
                  <p className="mt-1 text-sm text-zinc-400">{p.role}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* RESEARCH */}
      <Section className="bg-ink-900">
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <Eyebrow>
                <FlaskConical className="h-3.5 w-3.5" /> Research & science
              </Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Built on deep science
              </h2>
              <p className="mt-5 leading-relaxed text-zinc-300">
                My foundation is research — time at <strong>ISRO</strong>, a
                published <strong>IEEE</strong> paper, and work as a Generative AI
                Engineer at <strong>Mercedes-Benz R&D</strong>.
              </p>
              <p className="mt-4 leading-relaxed text-zinc-400">
                At DevAnanta, that science becomes products: graphene and carbon-based
                materials for high-performance, rare-earth-free batteries — and
                advanced coatings for defence and industry.
              </p>
            </Reveal>
            <Reveal delay={120}>
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <img
                  src="/img/isro.jpg"
                  alt="At the ISRO Space Applications Centre"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover object-[50%_35%]"
                />
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* SOCIAL IMPACT */}
      <Section>
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <img
                  src="/EmpoweringSociety.jpg"
                  alt="Engaging with rural families and children during the walk"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <Eyebrow>
                <HeartHandshake className="h-3.5 w-3.5" /> On the ground
              </Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Science with a soul of service
              </h2>
              <p className="mt-5 leading-relaxed text-zinc-300">
                I walked <strong>180+ km across 20+ villages</strong> for clean air,
                tree plantation, peace and cleanliness — listening to the people my
                technology is meant to serve.
              </p>
              <p className="mt-4 leading-relaxed text-zinc-400">
                Those conversations — with women farmers, self-help groups and rural
                families — shaped DevAnanta's mission. I'm a firm advocate for women's
                safety, women in STEM, and a founding team built on gender parity.
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* GALLERY */}
      <Section className="bg-ink-900">
        <Container>
          <Reveal>
            <SectionHeading eyebrow="In pictures" title="Moments from the journey" />
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {gallery.map((g, i) => (
              <Reveal key={g.src} delay={(i % 4) * 80}>
                <div className="group overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    decoding="async"
                    className="aspect-square w-full object-cover object-[50%_35%] transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section>
        <Container>
          <Reveal>
            <div className="rounded-3xl border border-brand-500/20 bg-gradient-to-br from-brand-600/20 via-ink-900 to-ink-900 px-6 py-14 text-center sm:px-12">
              <h2 className="mx-auto max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                See what we're building at DevAnanta R&D
              </h2>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Button href="https://devananta.com/" external>
                  Visit DevAnanta <ArrowUpRight className="h-4 w-4" />
                </Button>
                <Button href="/connect_me" variant="ghost">
                  Get in touch
                </Button>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      <Footer />
    </main>
  );
};

export default RecognitionPage;
